import { appendHostnameToRelativeUrl } from "./appendHostnameToRelativeUrl";
import { dataLayer } from "./dataLayer";
import { env } from "./env.mjs";
import { SubAssetType } from "./getSubAssetType";

const ENERGYDRINK_LCM_ID = "564d5b72-f768-45a7-9464-a6e3f8cacde9";
const SUPPORT_HUB_LCM_ID = "9b71a2a7-7124-4e10-ac35-c0c3dc727f49";
const DEFAULT_PCS_SOURCE = "product/company";
const SUPPORT_HUB_SOURCE = "support";
const SUPPORT_HUB_ROUTE = "/support-hub";

function getLcmId(route: string): string {
  if (route.includes(SUPPORT_HUB_ROUTE)) {
    return SUPPORT_HUB_LCM_ID;
  }

  return ENERGYDRINK_LCM_ID;
}

function getSource(route: string, gtmSource: string | null): string {
  if (gtmSource) {
    return gtmSource;
  }

  if (route.includes(SUPPORT_HUB_ROUTE)) {
    return SUPPORT_HUB_SOURCE;
  }

  return DEFAULT_PCS_SOURCE;
}

/**
 * Often we are checking if the environment has a GTM container inside each component,
 * Maybe we should move all checks here?
 *
 * if (!env.NEXT_PUBLIC_HAS_GTM_CONTAINER) {
 *   return null;
 * }
 */

export function startAnalytics({
  gtmSource,
  // gtmTemplate,
  title,
  route,
  localeString,
  subAssetType,
}: {
  gtmSource: string | null;
  // gtmTemplate: string;
  title: string | null;
  route: string;
  localeString: string;
  subAssetType: SubAssetType | null;
}) {
  dataLayer.push({
    "gtm.start": new Date().getTime(),
    event: "gtm.js",
  });

  const [country = null, language = null] = localeString.includes("-")
    ? localeString.split("-")
    : [];

  dataLayer.push({
    datalayer_version: "2.0.T",
    language,
    locale: country === "gb" ? "uk" : country,
    property_name: "Red Bull - BQ Master Rollup - nonRTM",
    page_template: "integration", // previously: `template: gtmTemplate || "integration"`
    asset_id: "unspecified",
    master_id_rb3: "unspecified",
    original_publishing_date: "unspecified",
    grouping_label_group: "unspecified",
    grouping_label: "unspecified",
    lcm_id: getLcmId(route),
    lucid_id: "unspecified",
    grouping_season: "unspecified",
    grouping_chapter: "unspecified",
    website_version: "rb3",
    sub_asset_type: subAssetType,
    local_custom_grouping: "unspecified",
    content_category: "unspecified",
    created_vs_translated: "unspecified",
    created_language_locale: "unspecified",
    content_language_locale: `${language}-${country === "gb" ? "uk" : country}`,
    source: getSource(route, gtmSource),
    geo_location: "unspecified",
    seo_title: title,
    disciplines_all: "unspecified",
    content_number_of_words: "unspecified",
    content_number_of_videos: "unspecified",
    content_number_of_social_embeddings: "unspecified",
    content_number_of_photos: "unspecified",
    publishing_date: "unspecified",
    related_events: "unspecified",
    related_people: "unspecified",
    playground_id: "unspecified",
    asset_translation_link: "unspecified",
  });

  dataLayer.push({
    event: "virtPath",
    virtPath: route,
  });
}

/**
 * GA4 click for "buy on Amazon" style buttons.
 *
 * 'value' : "Buy now on Amazon",
 * 'click_url' : "https://www.amazon.com/xyz...",
 * 'element_position' : "BlocksInteractiveHero",
 * 'method' : "Outbound: TRUE"
 * 'content_type' : "Button Click, Buy now"
 *
 *  * `value` and `clickUrl` are required to be passed, but can be `null` if there's no content.
 * No analytics event will be sent if any of the required parameters are missing.
 */
export function analyticsEcommerceVendorClick({
  value,
  clickUrl,
  elementPosition,
  method,
  contentType,
}: {
  value: string | null;
  clickUrl: string | null;
  elementPosition: string;
  method: "Outbound: TRUE" | "Outbound: FALSE"; // or `string` if gets more complex
  contentType: "Button Click, Buy now"; // or `string` if gets more complex
}) {
  if (!env.NEXT_PUBLIC_HAS_GTM_CONTAINER) {
    return null;
  }

  if (!value || !clickUrl || !elementPosition || !method || !contentType) {
    return null;
  }

  return dataLayer.push({
    event: "ga4Event",
    event_name: "click",
    click: {
      value: value,
      click_url: clickUrl,
      element_position: elementPosition,
      method: method,
      content_type: contentType,
    },
  });
}

/**
 * TODO: This is the same as the analyticsEcommerceVendorClick, apart from the "contentType".
 * Perhaps settings some kind of variant prop to set the contentType automatically?
 *
 * `value` and `clickUrl` are required to be passed, but can be `null` if there's no content.
 * No analytics event will be sent if any of the required parameters are missing.
 */
export function analyticsClick({
  value,
  clickUrl,
  elementPosition,
  method,
  contentType,
}: {
  value: string | null;
  clickUrl: string | null; // Relative URL as parameter, will be converted to full URL in the function
  elementPosition: string | null;
  method: "Outbound: TRUE" | "Outbound: FALSE"; // or `string` if gets more complex
  contentType: string; // Perhaps keep to a set of keywords like "CTA" for consistency
}) {
  if (!env.NEXT_PUBLIC_HAS_GTM_CONTAINER) {
    return null;
  }

  const payload = {
    event: "ga4Event",
    event_name: "click",
    click: {
      value: value,
      click_url: clickUrl ? appendHostnameToRelativeUrl(clickUrl) : null,
      element_position: elementPosition,
      method: method,
      content_type: contentType,
    },
  };

  console.log("click", payload);

  return dataLayer.push(payload);
}

/**
 *
 * @value The `product name`.
 * @returns
 */
export function analyticsFilter({
  value,
  contentType,
}: {
  value: string;
  contentType: "range" | "filter";
}) {
  if (!env.NEXT_PUBLIC_HAS_GTM_CONTAINER) {
    return null;
  }

  if (!value) {
    return null;
  }

  return dataLayer.push({
    event: "ga4Event",
    event_name: "filter",
    filter: {
      value: value,
      content_type: contentType,
    },
  });
}

export type AnalyticsPromotion = {
  id: string;
  name: string;
  creative: string;
  position: number;
};

/**
 * https://build.redbull.com/digital-analytics/red-bull-redbullcom#search
 */
export function analyticsViewSearchResults({
  value,
  contentType,
}: {
  value: string;
  contentType: string;
}) {
  if (!env.NEXT_PUBLIC_HAS_GTM_CONTAINER) {
    return null;
  }

  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return null;
  }

  const payload = {
    event: "ga4Event",
    event_name: "view_search_results",
    view_search_results: {
      value: trimmedValue,
      content_type: contentType,
    },
  };

  console.log("view_search_results", payload);

  return dataLayer.push(payload);
}

/**
 * https://build.redbull.com/digital-analytics/red-bull-redbullcom#module-tracking-elements
 */
export function analyticsViewPromotion({
  promotions,
  availableElements,
  promotionModule,
}: {
  promotions: AnalyticsPromotion[];
  availableElements: number;
  promotionModule: string;
}) {
  if (!env.NEXT_PUBLIC_HAS_GTM_CONTAINER) {
    return null;
  }

  if (promotions.length === 0 || availableElements <= 0) {
    return null;
  }

  const payload = {
    event: "view_promotion",
    ecommerce: {
      promotion_module: promotionModule,
      promotion_position_available: availableElements,
      items: promotions.map((promo) => ({
        promotion_id: promo.id,
        promotion_name: promo.name,
        creative_name: promo.creative,
        creative_slot: String(promo.position),
      })),
    },
  };

  console.log("view_promotion", payload);

  return dataLayer.push(payload);
}

/**
 * https://build.redbull.com/digital-analytics/red-bull-redbullcom#module-tracking-elements
 */
export function analyticsSelectPromotion({
  promotions,
  availableElements,
  promotionModule,
}: {
  promotions: AnalyticsPromotion[];
  availableElements: number;
  promotionModule: string;
}) {
  if (!env.NEXT_PUBLIC_HAS_GTM_CONTAINER) {
    return null;
  }

  if (promotions.length === 0 || availableElements <= 0) {
    return null;
  }

  const payload = {
    event: "select_promotion",
    ecommerce: {
      promotion_module: promotionModule,
      promotion_position_available: availableElements,
      items: promotions.map((promo) => ({
        promotion_id: promo.id,
        promotion_name: promo.name,
        creative_name: promo.creative,
        creative_slot: String(promo.position),
      })),
    },
  };

  console.log("select_promotion", payload);

  return dataLayer.push(payload);
}
